import { Ajax } from "../../Common/Ajax";
import { GlobalData } from "../../Common/GlobalData";
import { Confirm, Notification, PortfolioGridField, PortfolioGridFilter, TemplateHelpers } from "../../Common/KendoHelpers";
import { MultiSourceAnalysis } from "../../MultiSource/MultiSourceAnalysis";

export class SelectSymbols extends MultiSourceAnalysis {
    private static readonly FILTER_OWNED = 'owned';
    private static readonly SPINNER = '#spinner';
    private static readonly VL_FILES_UPLOAD = '#vlFilesUpload';

    private static errorMessage: string;

    constructor() {
        super('#grid');
        this.init(GlobalData.URL_BASE + GlobalData.URL_SUFFIX_IMPORT_DATA_VL_SELECT_SYMBOLS);
        this.initFileUpload();
    }

    protected override initFields() {
        super.initFields();

        this.fields['qtyOwn'] = new PortfolioGridField('qtyOwn');
        this.fields['selected'] = new PortfolioGridField('selected');
    }

    protected override initFilters() {
        super.initFilters();

        this.fields['qtyOwn'].addFilter(SelectSymbols.FILTER_OWNED,
            new PortfolioGridFilter('qtyOwn', 'gt', 0));
    }

    protected override getGridDataSource(): any {
        const baseDataSource = super.getGridDataSource();

        return {
            ...baseDataSource,
            schema: {
                ...baseDataSource.schema,
                model: {
                    ...baseDataSource.schema.model,
                    fields: {
                        ...baseDataSource.schema.model.fields,
                        qtyOwn: { type: 'number' },
                        selected: { type: 'boolean' },
                    }
                }
            }
        };
    }

    protected override getGridOptions(dataSource: any): kendo.ui.GridOptions {
        const options = super.getGridOptions(dataSource);
        options.dataBound = () => {
            super.gridDataBound();
            this.updateTotalSelected();
        };

        options.columns!.push({
            title: 'BI<br>Data',
            headerAttributes: { class: 'table-header-cell grid-header-14' },
            columns: [
                {
                    field: this.fields['qtyOwn'].getName(),
                    title: 'Qty<br>Own',
                    width: '4%',
                    filterable: true,
                    headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                    attributes: { 'class': 'table-cell-tight table-cell-center' },
                },
            ],
        });
        options.columns!.push({
            headerAttributes: { class: 'table-header-cell grid-header-14' },
            columns: [
                {
                    field: this.fields['selected'].getName(),
                    title: `Select`,
                    width: '4%',
                    filterable: true,
                    headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                    attributes: { 'class': 'table-cell-tight table-cell-center' },
                    template: kendo.template(TemplateHelpers.getCheckboxTemplate(this.fields['selected'].getName())),
                }
            ],
        });

        return options;
    }

    protected initFileUpload() {
        $(SelectSymbols.VL_FILES_UPLOAD).kendoUpload({
            async: {
                saveUrl: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IMPORT_DATA + '/save',
            },
            upload: this.onUpload,
            error: this.onError,
            complete: this.onComplete,
        }).data('kendoUpload');
    }

    protected override defineEvents() {
        super.defineEvents();

        $('#selectAllButton').off('click').on('click', () => {
            this.selectAllNone(true);
        });

        $('#selectNoneButton').off('click').on('click', () => {
            this.selectAllNone(false);
        });

        // Update value of the field when user clicks a checkbox.
        $('.selectedCheckbox').off('click').on('click', (e: any) => {
            this.selectedClick(e);
        });

        $('#finishedButton').off('click').on('click', (e: any) => {
            this.finishedClick(e);
        });
    }

    protected override updateFilter(value: string): boolean {
        if (!super.updateFilter(value) && value == 'owned') {
            this.filterSet.set(SelectSymbols.FILTER_OWNED);
            return true;
        }

        return false;
    }

    private selectAllNone(selectAll: boolean) {
        const onDone = () => {
            const success = () => {
                this.grid.dataSource.read();
            };

            const error = (x: any) => {
                var msg = new Notification();
                msg.error(x.responseJSON);
            };

            const visibleRows = this.grid.dataSource.view();
            const symbols = visibleRows.map(function (row: any) {
                return row.symbol;
            });

            Ajax.invoke(GlobalData.URL_SUFFIX_IMPORT_DATA_VL_SELECT_SYMBOLS +
                `/selectAllNone?selectAll=${selectAll}`,
                'PUT', symbols, success, error);
        }

        const onFail = () => {
            // Do nothing
        };

        const confirm = new Confirm(
            `Are you sure you want to ${selectAll ? "" : "un"}select the currently visible stocks?`,
            onDone, onFail);
        confirm.show();
    }

    private selectedClick(e: any) {
        let dataItem = this.getClosestDataItem(e) as any;
        dataItem.selected = e.target.checked;

        this.updateSelected(dataItem);
    }

    private updateSelected(dataItem: any) {
        let success = () => {
            this.grid.dataSource.read();
            this.updateTotalSelected();
        };

        let error = (x: any) => {
            var msg = new Notification();
            msg.error(x.responseJSON);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_IMPORT_DATA_VL_SELECT_SYMBOLS +
            '?symbol=' + dataItem.symbol + '&selected=' + dataItem.selected,
            'PUT', null, success, error);
    }

    private updateTotalSelected() {
        const data = this.grid.dataSource.data();
        let totalSelected = 0;
        for (let i = 0; i < data.length; ++i) {
            if (data[i].selected) {
                ++totalSelected;
            }
        }
        $('#totalSelected').html(totalSelected.toString());
    }

    private finishedClick(e: any) {
        const onDone = () => {
            const success = () => {
                this.grid.dataSource.read();
            };

            const error = (x: any) => {
                var msg = new Notification();
                msg.error(x.responseJSON);
            };

            Ajax.invoke(GlobalData.URL_SUFFIX_IMPORT_DATA_VL_SELECT_SYMBOLS,
                'DELETE', null, success, error);
        };

        const onFail = () => {
            // Do nothing
        };

        const confirm = new Confirm(
            'Are you sure you have completed the selection process?',
            onDone, onFail);
        confirm.show();
    }

    private getClosestDataItem(e: any) {
        return this.grid.dataItem($(e.target).closest('tr'));
    }

    private onUpload(e: any) {
        $(SelectSymbols.SPINNER).show();
        SelectSymbols.errorMessage = '';
    }

    private onError(e: any) {
        if (e && e.XMLHttpRequest && e.files && e.files.length) {
            let status = e.XMLHttpRequest.status;
            let fileName = e.files[0].name;

            switch (status) {
                case 400:
                    SelectSymbols.errorMessage += '<br>Exception thrown: ' + e.XMLHttpRequest.responseText;
                    break;
                case 401:
                    SelectSymbols.errorMessage = '<br>Unauthorized';
                    break;
                case 404:
                    SelectSymbols.errorMessage += '<br>Unsupported file ' + fileName;
                    break;
                default:
                    SelectSymbols.errorMessage += '<br>Error code ' + status +
                        ' returned for file ' + fileName;
            }
        }
    }

    private onComplete() {
        $(SelectSymbols.SPINNER).hide();
        var notification = new Notification();

        if (SelectSymbols.errorMessage.length == 0) {
            notification.success('All files imported successfully');
        } else {
            notification.error(SelectSymbols.errorMessage);
        }
    }
}
